import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, lastValueFrom, Observable, switchMap } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, take, takeWhile } from 'rxjs/operators';
import { NetzzuordnungService } from '../../service/netzzuordnung/netzzuordnung.service';
import { ErzeugungsanlageFilter } from '../../service/erzeugungsanlage/erzeugungsanlage-filter';
import { ErzeugungsanlageService } from '../../service/erzeugungsanlage/erzeugungsanlage.service';
import { ActivatedRoute } from '@angular/router';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { ErzeugungsanlageExportService } from '../../service/erzeugungsanlage/erzeugungsanlage-export.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { Energietraeger } from '../../domain/model/energietraeger';
import { EnergietraegerPipe } from '../../common/pipe/energietraeger.pipe';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { inject } from '@angular/core';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'redispatch-erzeugungsanlage-filter',
    templateUrl: './erzeugungsanlage-filter.component.html',
    styleUrls: [
        './erzeugungsanlage-filter.component.scss',
    ],
    standalone: false,
})
export class ErzeugungsanlageFilterComponent implements OnInit, OnDestroy {

    @Input() initialFilter?: ErzeugungsanlageFilter;
    @Output() filter = new EventEmitter<ErzeugungsanlageFilter>();

    stadtwerkeId?: string;
    filterFormGroup: UntypedFormGroup;

    public profile: TnbUser;
    public readonly Energietraeger = Energietraeger;

    filteredNetzbetreiber$: Observable<string[]>;
    filteredTeilgebiete$: Observable<string[]>;

    filteredDokumentenId$: Observable<string[]>;
    filteredSteuerbareResourceId$: Observable<string[]>;
    filteredTechnischeResourceId$: Observable<string[]>;
    filteredBezVnbIntern$: Observable<string[]>;
    filteredOrtsnamen$: Observable<string[]>;
    filteredStationsnamen$: Observable<string[]>;
    filteredTrafoFeldName$: Observable<string[]>;
    installierteLeistungMin = 0;
    installierteLeistungMax = 10000000;

    protected energietraegerPipe = inject(EnergietraegerPipe);
    private netzbetreiberService = inject(NetzbetreiberService);
    private route = inject(ActivatedRoute);
    private netzzuordnungService = inject(NetzzuordnungService);
    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlageExportService = inject(ErzeugungsanlageExportService);
    private formBuilder = inject(UntypedFormBuilder);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    private allNetzbetreiberForWestnetzView$: Observable<Netzbetreiber[]>;
    private componentDestroyed = false;

    async ngOnInit() {
        this.profile = this.route.snapshot.data.profile;
        if (this.profile.netzbetreiberType !== 'WESTNETZ') {
            this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
        }
        this.allNetzbetreiberForWestnetzView$ =
            this.netzbetreiberService.getAlleNetzbetreiber()
                .pipe(
                    take(1),
                    shareReplay(1),
                );

        // Build form and set initial values
        this.filterFormGroup = this.formBuilder.group({
            netzbetreiber: [
                this.initialFilter.netzbetreiberName,
            ],
            teilgebiet: [
                this.initialFilter.netzbetreiberTeilgebiet,
            ],
            dokumentenId: [
                this.initialFilter.dokumentenId,
            ],
            steuerbareResourceId: [
                this.initialFilter.steuerbareResourceId,
            ],
            technischeResourceId: [
                this.initialFilter.technischeResourceId,
            ],
            bezVnbIntern: [
                this.initialFilter.bezVnbIntern,
            ],
            ortsname: [
                this.initialFilter.ortsname,
            ],
            stationsname: [
                this.initialFilter.stationsname,
            ],
            trafoFeldName: [
                this.initialFilter.trafoFeldName,
            ],
            energietraeger: [
                this.initialFilter.energietraeger,
            ],
            status: [
                this.initialFilter.status,
            ],
            installierteLeistungMin: [
                this.initialFilter.installierteLeistungMin,
            ],
            installierteLeistungMax: [
                this.initialFilter.installierteLeistungMax,
            ],
        });

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new ErzeugungsanlageFilter(
                        val.netzbetreiber,
                        val.teilgebiet,
                        val.dokumentenId,
                        val.steuerbareResourceId,
                        val.technischeResourceId,
                        val.bezVnbIntern,
                        this.stadtwerkeId,
                        val.ortsname,
                        val.stationsname,
                        val.trafoFeldName,
                        val.energietraeger,
                        val.status,
                        val.installierteLeistungMin,
                        val.installierteLeistungMax,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredNetzbetreiber$ = this.filterFormGroup.get('netzbetreiber').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiber').value),
                map(name => name?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(name => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            name ? allNetzbetreiber.filter(n => n.name.toLowerCase().includes(name)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.name)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTeilgebiete$ = this.filterFormGroup.get('teilgebiet').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('teilgebiet').value),
                map(teilgebiet => teilgebiet?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(teilgebiet => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            teilgebiet ? allNetzbetreiber.filter(n => n.teilgebiet.toLowerCase().includes(teilgebiet)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.teilgebiet)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredDokumentenId$ = this.filterFormGroup.get('dokumentenId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('dokumentenId').value),
                distinctUntilChanged(),
                switchMap(id => this.erzeugungsanlageService.getDokumentenId(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredSteuerbareResourceId$ = this.filterFormGroup.get('steuerbareResourceId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('steuerbareResourceId').value),
                distinctUntilChanged(),
                switchMap(id => this.erzeugungsanlageService.getSteuerbareResourceId(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTechnischeResourceId$ = this.filterFormGroup.get('technischeResourceId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('technischeResourceId').value),
                distinctUntilChanged(),
                switchMap(id => this.erzeugungsanlageService.getTechnischeResourceId(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredBezVnbIntern$ = this.filterFormGroup.get('bezVnbIntern').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('bezVnbIntern').value),
                distinctUntilChanged(),
                switchMap(bezeichnung => this.erzeugungsanlageService.getBezVnbIntern(bezeichnung, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredOrtsnamen$ = this.filterFormGroup.get('ortsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('ortsname').value),
                distinctUntilChanged(),
                switchMap(ort => this.netzzuordnungService.getOrtsnamen(
                    this.stadtwerkeId,
                    ort,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStationsnamen$ = this.filterFormGroup.get('stationsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stationsname').value),
                distinctUntilChanged(),
                switchMap(stationsname => this.netzzuordnungService.getStationsnamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    stationsname,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTrafoFeldName$ = this.filterFormGroup.get('trafoFeldName').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('trafoFeldName').value),
                distinctUntilChanged(),
                switchMap(trafoFeldName => this.netzzuordnungService.getTrafoFeldNamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    this.filterFormGroup.get('stationsname').value,
                    trafoFeldName,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        const minMax = await lastValueFrom(this.erzeugungsanlageService.getInstallierteLeistungRange(this.stadtwerkeId));
        const minControl = this.filterFormGroup.get('installierteLeistungMin');
        minControl.setValue(minControl.value ?? minMax.min);
        const maxControl = this.filterFormGroup.get('installierteLeistungMax');
        maxControl.setValue(maxControl.value ?? minMax.max);
        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    public get isWetznetzUser() {
        return this.profile.netzbetreiberType === 'WESTNETZ';
    }

    public exportErzeugungsanlagenCSV() {
        this.erzeugungsanlageExportService.exportErzeugungsanlagenCSV(
            this.teilgebietSelectionService.getSelectedTeilgebiet(),
        );
    }
}
